import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

function Login() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, registerTenant, googleLogin } = useAuth();
  const navigate = useNavigate();

  const googleClientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

  // Load saved credentials on mount
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    const savedPassword = localStorage.getItem('remembered_password');
    if (savedEmail) {
      setLoginForm(prev => ({
        ...prev,
        email: savedEmail,
        password: savedPassword || ''
      }));
      setRememberMe(true);
    }
  }, []);

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Registration form state
  const [registerForm, setRegisterForm] = useState({
    businessName: '',
    industry: 'customer_service',
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(loginForm.email, loginForm.password);

    if (result.success) {
      // Save or remove credentials based on Remember Me
      if (rememberMe) {
        localStorage.setItem('remembered_email', loginForm.email);
        localStorage.setItem('remembered_password', loginForm.password);
      } else {
        localStorage.removeItem('remembered_email');
        localStorage.removeItem('remembered_password');
      }
      toast.success(t('success.welcomeBack'));
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error(t('errors.passwordsDontMatch'));
      return;
    }

    setLoading(true);

    const result = await registerTenant(
      {
        business_name: registerForm.businessName,
        industry: registerForm.industry
      },
      {
        email: registerForm.email,
        password: registerForm.password,
        name: registerForm.name
      }
    );

    if (result.success) {
      toast.success(t('success.accountCreated'));
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (credentialResponse.credential) {
      setLoading(true);
      const result = await googleLogin(credentialResponse.credential);

      if (result.success) {
        toast.success(t('success.welcome'));
        navigate('/');
      } else {
        toast.error(result.error);
      }
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error(t('errors.googleLoginFailed'));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="qomplement Voice"
            className="h-10 mx-auto"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center gap-2">
            <div className="h-10 w-10 bg-primary rounded-xl flex items-center justify-center">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-foreground">qomplement Voice</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 text-center">
            <h2 className="text-2xl font-bold text-foreground">{t('login.getStarted')}</h2>
            <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
              {t('login.signInOrCreate')}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            {/* Tabs */}
            <div className="flex rounded-lg bg-secondary p-1 mb-6">
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'login'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('login.login')}
              </button>
              <button
                onClick={() => setActiveTab('signup')}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'signup'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('login.signUp')}
              </button>
            </div>

            {/* Login Form */}
            {activeTab === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="login-email" className="block text-sm font-medium text-foreground">
                    {t('login.emailLabel')}
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      required
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="name@example.com"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label htmlFor="login-password" className="block text-sm font-medium text-foreground">
                    {t('login.passwordLabel')}
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="block w-full pl-10 pr-10 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder={t('login.enterPassword')}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                      style={{ color: 'var(--muted)' }}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 text-primary focus:ring-primary border-border rounded cursor-pointer"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-foreground cursor-pointer">
                      {t('login.rememberCredentials')}
                    </label>
                  </div>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    {t('login.forgotPassword')}
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary hover:bg-primary-hover text-white font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>
                      {t('login.signInBtn')}
                      <ArrowRightIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Signup Form */}
            {activeTab === 'signup' && (
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Business Name */}
                <div className="space-y-1.5">
                  <label htmlFor="business-name" className="block text-sm font-medium text-foreground">
                    {t('login.businessName')}
                  </label>
                  <div className="relative">
                    <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="business-name"
                      name="businessName"
                      type="text"
                      required
                      value={registerForm.businessName}
                      onChange={(e) => setRegisterForm({ ...registerForm, businessName: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder={t('login.yourCompany')}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Industry */}
                <div className="space-y-1.5">
                  <label htmlFor="industry" className="block text-sm font-medium text-foreground">
                    {t('login.industry')}
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={registerForm.industry}
                    onChange={(e) => setRegisterForm({ ...registerForm, industry: e.target.value })}
                    className="block w-full px-3 py-2.5 border border-border rounded-lg text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    disabled={loading}
                  >
                    <option value="customer_service">{t('login.customerService')}</option>
                    <option value="debt_collection">{t('login.debtCollection')}</option>
                    <option value="healthcare">{t('login.healthcare')}</option>
                    <option value="retail">{t('login.retail')}</option>
                    <option value="other">{t('login.other')}</option>
                  </select>
                </div>

                {/* Full Name */}
                <div className="space-y-1.5">
                  <label htmlFor="name" className="block text-sm font-medium text-foreground">
                    {t('login.fullName')}
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="John Doe"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="register-email" className="block text-sm font-medium text-foreground">
                    {t('login.emailLabel')}
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="register-email"
                      name="email"
                      type="email"
                      required
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="name@example.com"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label htmlFor="register-password" className="block text-sm font-medium text-foreground">
                    {t('login.passwordLabel')}
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="register-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={8}
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      className="block w-full pl-10 pr-10 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder={t('login.createPasswordPlaceholder')}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                      style={{ color: 'var(--muted)' }}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground">
                    {t('login.confirmPassword')}
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--muted)' }} />
                    <input
                      id="confirm-password"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-foreground bg-card placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder={t('login.confirmPasswordPlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary hover:bg-primary-hover text-white font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>
                      {t('login.createAccount')}
                      <ArrowRightIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Footer - Google Login */}
          <div className="px-6 pb-6 space-y-4">
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2" style={{ color: 'var(--muted)' }}>{t('login.orContinueWith')}</span>
              </div>
            </div>

            {/* Google Login Button */}
            <div className="flex justify-center">
              {googleClientId ? (
                <GoogleOAuthProvider clientId={googleClientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    shape="rectangular"
                    size="large"
                    width="100%"
                    locale="en"
                  />
                </GoogleOAuthProvider>
              ) : (
                <span className="text-sm" style={{ color: 'var(--muted)' }}>
                  {t('login.googleNotConfigured')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
